/**
 * D-ID Avatar Realtime API Integration
 */
import { AVATAR_REALTIME_URL } from "../../config/apiConfig";

const API_URL = AVATAR_REALTIME_URL;

/**
 * Create a new agent stream
 * @returns {Promise<{id: string, offer: object, ice_servers: array}>}
 */
export async function createAgentStream() {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "create_agent_stream",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(
        errorData.error || errorData.message || `Failed to create stream: ${response.status}`
      );
    }

    const data = await response.json();
    
    
    
    if (!data.id || !data.offer) {
      console.error("Invalid response structure:", data);
      throw new Error("Invalid response: missing id or offer");
    }

    return {
      id: data.id,
      offer: data.offer,
      ice_servers: data.ice_servers || [],
    };
  } catch (error) {
    console.error("Error creating agent stream:", error);
    throw error;
  }
}

/**
 * Submit SDP Answer
 * @param {string} streamId - The stream ID from create_agent_stream
 * @param {object} answer - SDP answer object with type and sdp
 * @returns {Promise<object>}
 */
export async function submitSdpAnswer(streamId, answer) {
  try {
    if (!streamId) {
      throw new Error("streamId is required");
    }
    if (!answer || !answer.type || !answer.sdp) {
      throw new Error("answer must have type and sdp properties");
    }

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "submit_sdp",
        streamId: streamId,
        answer: {
          type: answer.type,
          sdp: answer.sdp,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      const errorMessage = errorData.error || errorData.message || `Failed to submit SDP: ${response.status}`;
      const error = new Error(errorMessage);
      error.isStreamNotFound = errorMessage.includes("Stream session not found") || 
                               errorMessage.includes("Create agent stream first");
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error submitting SDP answer:", error);
    throw error;
  }
}

/**
 * Submit ICE Candidate
 * @param {string} streamId - The stream ID
 * @param {string} candidate - ICE candidate string
 * @param {string} sdpMid - SDP media stream identification
 * @param {number} sdpMLineIndex - SDP media line index
 * @returns {Promise<object>}
 */
export async function submitIceCandidate(streamId, candidate, sdpMid, sdpMLineIndex) {
  try {
    if (!streamId) {
      throw new Error("streamId is required");
    }
    if (!candidate) {
      throw new Error("candidate is required");
    }

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "submit_ice",
        streamId: streamId,
        candidate: candidate,
        sdpMid: sdpMid || "a",
        sdpMLineIndex: sdpMLineIndex || 0,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      const errorMessage = errorData.error || errorData.message || `Failed to submit ICE candidate: ${response.status}`;
      const error = new Error(errorMessage);
      error.isStreamNotFound = errorMessage.includes("Stream session not found") || 
                               errorMessage.includes("Create agent stream first");
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error submitting ICE candidate:", error);
    throw error;
  }
}

/**
 * Send text content to the agent
 * @param {string} streamId - The stream ID
 * @param {string} text - The text message to send
 * @returns {Promise<object>}
 */
export async function sendTextContent(streamId, text) {
  try {
    if (!streamId) {
      throw new Error("streamId is required");
    }
    if (!text || typeof text !== "string") {
      throw new Error("text must be a non-empty string");
    }

    const requestBody = {
      action: "send_content",
      streamId: streamId,
      contentType: "text",
      text: text,
    };

   

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      
      console.error("Error response from send_text:", {
        status: response.status,
        statusText: response.statusText,
        errorData,
        streamId,
      });
      
      // Preserve the original error message for stream session errors
      const errorMessage = errorData.error || errorData.message || `Failed to send text: ${response.status}`;
      
      // Throw error with original message so it can be detected
      const error = new Error(errorMessage);
      error.isStreamNotFound = errorMessage.includes("Stream session not found") || 
                               errorMessage.includes("Create agent stream first");
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending text content:", error);
    throw error;
  }
}

/**
 * Send audio content to the agent
 * @param {string} streamId - The stream ID
 * @param {string} audioBase64 - Base64 encoded audio data
 * @returns {Promise<object>}
 */
export async function sendAudioContent(streamId, audioBase64) {
  try {
    if (!streamId) {
      throw new Error("streamId is required");
    }
    if (!audioBase64 || typeof audioBase64 !== "string") {
      throw new Error("audioBase64 must be a non-empty string");
    }

    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "send_content",
        streamId: streamId,
        contentType: "audio",
        audio_base64: audioBase64,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(
        errorData.error || errorData.message || `Failed to send audio: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending audio content:", error);
    throw error;
  }
}

/**
 * Helper function to convert audio blob to base64
 * @param {Blob} audioBlob - Audio blob to convert
 * @returns {Promise<string>} Base64 encoded audio string
 */
export async function audioBlobToBase64(audioBlob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove data URL prefix (e.g., "data:audio/wav;base64,")
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(audioBlob);
  });
}

/**
 * Helper function to convert audio buffer to base64
 * @param {ArrayBuffer} audioBuffer - Audio buffer to convert
 * @param {string} mimeType - MIME type (default: "audio/wav")
 * @returns {Promise<string>} Base64 encoded audio string
 */
export async function audioBufferToBase64(audioBuffer, mimeType = "audio/wav") {
  const blob = new Blob([audioBuffer], { type: mimeType });
  return audioBlobToBase64(blob);
}
